import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import {
  ShieldCheckIcon,
  BoltIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChartBarIcon,
  DocumentTextIcon,
  PlayIcon,
  PauseIcon,
  Cog6ToothIcon,
  SparklesIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  InformationCircleIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";
import {
  getAutoComplianceTasksApi,
  runAutoComplianceTaskApi,
  updateAutoComplianceTaskApi,
  getAutoComplianceSummaryApi,
} from "../../services/axios/AuthApis/AutoComplianceApis";
import { DataTable } from "../common/DataTable";

// ── Status helpers ──────────────────────────────────────────────────────────

const STATUS_META = {
  compliant: {
    label: "Compliant",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10 border-emerald-500/30",
    icon: CheckCircleIcon,
  },
  in_progress: {
    label: "In Progress",
    color: "text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/30",
    icon: ClockIcon,
  },
  non_compliant: {
    label: "Non-Compliant",
    color: "text-red-400",
    bg: "bg-red-500/10 border-red-500/30",
    icon: ExclamationTriangleIcon,
  },
  pending: {
    label: "Pending",
    color: "text-amber-400",
    bg: "bg-amber-500/10 border-amber-500/30",
    icon: ClockIcon,
  },
  automated: {
    label: "Automated",
    color: "text-purple-400",
    bg: "bg-purple-500/10 border-purple-500/30",
    icon: BoltIcon,
  },
};

const PRIORITY_META = {
  critical: { label: "Critical", color: "text-red-400", dot: "bg-red-400" },
  high: { label: "High", color: "text-orange-400", dot: "bg-orange-400" },
  medium: { label: "Medium", color: "text-amber-400", dot: "bg-amber-400" },
  low: { label: "Low", color: "text-emerald-400", dot: "bg-emerald-400" },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.bg} ${meta.color}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const PriorityDot = ({ priority }) => {
  const meta = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${meta.color}`}>
      <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

// ── Stat card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 flex items-center gap-4"
  >
    <div className={`p-2.5 rounded-lg ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-white">{value ?? "—"}</p>
      <p className="text-xs text-slate-400">{label}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </motion.div>
);

// ── Task detail drawer ────────────────────────────────────────────────────

const TaskDrawer = ({ task, onClose, onRun, running, onStatusChange }) => {
  if (!task) return null;
  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      <motion.div
        key="drawer"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 220 }}
        className="fixed right-0 top-0 h-full w-full max-w-md bg-slate-900 border-l border-slate-700 z-50 flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-700">
          <div className="flex-1 min-w-0 pr-3">
            <div className="flex items-center gap-2 mb-1">
              <StatusBadge status={task.status} />
              <PriorityDot priority={task.priority} />
            </div>
            <h2 className="text-base font-semibold text-white leading-snug">{task.title}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{task.framework} · {task.category}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Description */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Description</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{task.description || "No description available."}</p>
          </div>

          {/* AI Recommendation */}
          {task.ai_recommendation && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <LightBulbIcon className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-semibold text-blue-400 uppercase tracking-wide">AI Recommendation</span>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{task.ai_recommendation}</p>
            </div>
          )}

          {/* Automation steps */}
          {task.automation_steps?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Automation Steps</h3>
              <ol className="space-y-2">
                {task.automation_steps.map((step, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-slate-300">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-400 font-medium mt-0.5">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Status override */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Override Status</h3>
            <select
              value={task.status}
              onChange={(e) => onStatusChange(task.id, e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-blue-500"
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="compliant">Compliant</option>
              <option value="non_compliant">Non-Compliant</option>
              <option value="automated">Automated</option>
            </select>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Control Code", value: task.control_code },
              { label: "Owner", value: task.owner || "Unassigned" },
              { label: "Due Date", value: task.due_date || "Not set" },
              { label: "Last Run", value: task.last_run || "Never" },
            ].map(({ label, value }) => (
              <div key={label} className="bg-slate-800 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-0.5">{label}</p>
                <p className="text-sm font-medium text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex gap-3">
          <button
            onClick={() => onRun(task.id)}
            disabled={running === task.id}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {running === task.id ? (
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
            ) : (
              <BoltIcon className="w-4 h-4" />
            )}
            {running === task.id ? "Running…" : "Run Automation"}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

const AutomateCompliance = () => {
  const { user } = useAuth();
  const customerId = user?.customer_id || user?.id;

  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [running, setRunning] = useState(null); // task id currently running
  const [runningAll, setRunningAll] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterFramework, setFilterFramework] = useState("all");

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [tasksRes, summaryRes] = await Promise.all([
        getAutoComplianceTasksApi(customerId),
        getAutoComplianceSummaryApi(customerId),
      ]);
      setTasks(tasksRes?.tasks || []);
      setSummary(summaryRes || null);
    } catch (err) {
      console.error("AutomateCompliance fetch error", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Run automation ────────────────────────────────────────────────────────

  const handleRun = async (taskId) => {
    setRunning(taskId);
    try {
      const res = await runAutoComplianceTaskApi(customerId, taskId);
      // Update task in list
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, ...res.task } : t))
      );
    } catch (err) {
      console.error("Run automation error", err);
    } finally {
      setRunning(null);
    }
  };

  // ── Run all non-compliant ─────────────────────────────────────────────

  const handleRunAll = async () => {
    const targets = tasks.filter((t) => t.status !== "compliant" && t.status !== "automated");
    if (targets.length === 0) return;
    setRunningAll(true);
    for (const task of targets) {
      try {
        const res = await runAutoComplianceTaskApi(customerId, task.id);
        setTasks((prev) =>
          prev.map((t) => (t.id === task.id ? { ...t, ...res.task } : t))
        );
      } catch (err) {
        console.error("Run all error for task", task.id, err);
      }
    }
    setRunningAll(false);
  };

  // ── Status override (customer_admin) ─────────────────────────────────────

  const handleStatusChange = async (taskId, newStatus) => {
    // Optimistic update
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus, progress: newStatus === "compliant" || newStatus === "automated" ? 100 : t.progress } : t))
    );
    if (selectedTask?.id === taskId) {
      setSelectedTask((prev) => ({ ...prev, status: newStatus }));
    }
    try {
      await updateAutoComplianceTaskApi(taskId, { status: newStatus, customer_id: customerId });
    } catch (err) {
      console.error("Status update error", err);
    }
  };

  // ── Filter / search ────────────────────────────────────────────────────

  const frameworks = [...new Set(tasks.map((t) => t.framework).filter(Boolean))];

  const filtered = tasks.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      t.title?.toLowerCase().includes(q) ||
      t.framework?.toLowerCase().includes(q) ||
      t.control_code?.toLowerCase().includes(q);
    const matchStatus = filterStatus === "all" || t.status === filterStatus;
    const matchPriority = filterPriority === "all" || t.priority === filterPriority;
    const matchFramework = filterFramework === "all" || t.framework === filterFramework;
    return matchSearch && matchStatus && matchPriority && matchFramework;
  });

  const taskColumns = [
    {
      id: "task",
      header: "Control / Framework",
      cell: ({ row }) => (
        <div>
          <p className="text-sm font-medium text-white">{row.original.title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{row.original.framework} · {row.original.category}</p>
        </div>
      ),
    },
    {
      accessorKey: "control_code",
      header: "Code",
      cell: ({ getValue }) => <span className="text-xs text-slate-400">{getValue()}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    {
      accessorKey: "priority",
      header: "Priority",
      cell: ({ getValue }) => <PriorityDot priority={getValue()} />,
    },
    {
      id: "progress",
      header: "Progress",
      cell: ({ row }) => (
        <div>
          <div className="w-full bg-slate-700 rounded-full h-1.5">
            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${row.original.progress ?? 0}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{row.original.progress ?? 0}%</p>
        </div>
      ),
    },
    {
      accessorKey: "due_date",
      header: "Due Date",
      cell: ({ getValue }) => <span className="text-xs text-slate-500">{getValue() || "—"}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const task = row.original;
        return (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setSelectedTask(task)}
              className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              title="View details"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleRun(task.id)}
              disabled={running === task.id}
              className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Run automation"
            >
              {running === task.id ? (
                <ArrowPathIcon className="w-4 h-4 animate-spin text-blue-400" />
              ) : (
                <BoltIcon className="w-4 h-4" />
              )}
            </button>
          </div>
        );
      },
    },
  ];

  // ── Computed summary ────────────────────────────────────────────────────

  const localSummary = {
    total: tasks.length,
    compliant: tasks.filter((t) => t.status === "compliant").length,
    in_progress: tasks.filter((t) => t.status === "in_progress").length,
    non_compliant: tasks.filter((t) => t.status === "non_compliant").length,
    automated: tasks.filter((t) => t.status === "automated").length,
    score: summary?.compliance_score ?? (tasks.length
      ? Math.round((tasks.filter((t) => t.status === "compliant").length / tasks.length) * 100)
      : 0),
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="w-8 h-8 text-blue-400 animate-spin" />
          <p className="text-slate-400 text-sm">Loading compliance tasks…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <BoltIcon className="w-5 h-5 text-blue-400" />
            <h1 className="text-xl font-bold text-white">Automate Compliance</h1>
          </div>
          <p className="text-sm text-slate-400">
            AI-powered automation engine for your compliance controls
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRunAll}
            disabled={runningAll || tasks.filter((t) => t.status !== "compliant" && t.status !== "automated").length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            title="Run automation on all non-compliant controls"
          >
            {runningAll ? (
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
            ) : (
              <BoltIcon className="w-4 h-4" />
            )}
            {runningAll ? "Running All…" : "Run All"}
          </button>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-sm text-slate-300 rounded-lg transition-colors disabled:opacity-60"
          >
            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <StatCard
          icon={ShieldCheckIcon}
          label="Compliance Score"
          value={`${localSummary.score}%`}
          color="bg-blue-600"
        />
        <StatCard
          icon={CheckCircleIcon}
          label="Compliant"
          value={localSummary.compliant}
          color="bg-emerald-600"
        />
        <StatCard
          icon={BoltIcon}
          label="Automated"
          value={localSummary.automated}
          color="bg-purple-600"
        />
        <StatCard
          icon={ClockIcon}
          label="In Progress"
          value={localSummary.in_progress}
          color="bg-sky-600"
        />
        <StatCard
          icon={ExclamationTriangleIcon}
          label="Non-Compliant"
          value={localSummary.non_compliant}
          color="bg-red-600"
        />
      </div>

      {/* Progress bar */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-300">Overall Compliance Progress</span>
          <span className="text-sm font-bold text-white">{localSummary.score}%</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-3">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${localSummary.score}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`h-3 rounded-full ${
              localSummary.score >= 80
                ? "bg-emerald-500"
                : localSummary.score >= 50
                ? "bg-amber-500"
                : "bg-red-500"
            }`}
          />
        </div>
        <div className="flex flex-wrap gap-4 mt-3">
          {Object.entries({
            Compliant: { value: localSummary.compliant, color: "text-emerald-400" },
            "In Progress": { value: localSummary.in_progress, color: "text-blue-400" },
            "Non-Compliant": { value: localSummary.non_compliant, color: "text-red-400" },
            Automated: { value: localSummary.automated, color: "text-purple-400" },
          }).map(([k, { value, color }]) => (
            <span key={k} className={`text-xs font-medium ${color}`}>
              {k}: <span className="font-bold">{value}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search controls, frameworks…"
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-blue-500"
        >
          <option value="all">All Statuses</option>
          <option value="compliant">Compliant</option>
          <option value="in_progress">In Progress</option>
          <option value="non_compliant">Non-Compliant</option>
          <option value="automated">Automated</option>
          <option value="pending">Pending</option>
        </select>

        {/* Priority filter */}
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-blue-500"
        >
          <option value="all">All Priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        {/* Framework filter */}
        {frameworks.length > 0 && (
          <select
            value={filterFramework}
            onChange={(e) => setFilterFramework(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Frameworks</option>
            {frameworks.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        )}
      </div>

      {/* Table */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <ShieldCheckIcon className="w-10 h-10 text-slate-600" />
            <p className="text-slate-400 text-sm font-medium">
              {tasks.length === 0 ? "No compliance tasks found" : "No tasks match your filters"}
            </p>
            {tasks.length === 0 && (
              <p className="text-slate-500 text-xs text-center max-w-xs">
                Compliance tasks are generated from your assigned frameworks in the Trust Center.
              </p>
            )}
          </div>
        ) : (
          <DataTable
            columns={taskColumns}
            data={filtered}
            getRowId={(task) => String(task.id)}
            emptyMessage="No tasks match your filters"
            enableColumnVisibility={false}
            enableColumnResizing
            maxBodyHeight="65vh"
            headerClassName="bg-slate-800/80 text-xs font-semibold uppercase tracking-wide text-slate-400"
            className="border-0"
          />
        )}

        {/* Footer count */}
        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-700 text-xs text-slate-500">
            Showing {filtered.length} of {tasks.length} tasks
          </div>
        )}
      </div>

      {/* Detail drawer */}
      <TaskDrawer
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        onRun={handleRun}
        running={running}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
};

export default AutomateCompliance;
